import { getDefaultStore, useDefaultStore } from '@/data/Content/DefaultStore';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { dataMap, fetcher } from '@/data/Content/_Inventory';
import {
	dataMap as dataMapPBC,
	fetcher as fetcherPBC,
	getSWRKey as getSWRKey_PBC,
} from '@/data/Content/_InventoryPBC';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getLocalization } from '@/data/Localization';
import { getSettings, Settings, useSettings } from '@/data/Settings';
import { STRING_TRUE } from '@/data/constants/catalog';
import { DATA_KEY_INVENTORY } from '@/data/constants/dataKey';
import {
	INVENTORY_DEDUPING_INTERVAL,
	INVENTORY_PBC,
	INVENTORY_PBC_EXT_FFM_ID,
} from '@/data/constants/inventory';
import { Cache } from '@/data/types/Cache';
import { InventoryResponse } from '@/data/types/ProductAvailabilityData';
import { StoreDetails } from '@/data/types/Store';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';
import useSWR, { unstable_serialize } from 'swr';

export type HDMInventorySWRKeyProps = {
	settings: Settings;
	partNumber?: string;
	productIds?: string;
	langId?: string;
	physicalStoreUniqueId?: string;
	search?: number;
};
// Change OOB physicalStoreName to physicalStoreId
const getSWRKey = ({
	settings,
	partNumber,
	productIds,
	langId,
	physicalStoreUniqueId,
	search,
}: HDMInventorySWRKeyProps) => {
	const { storeId } = settings;
	if ((!partNumber && !productIds) || !storeId || !physicalStoreUniqueId) return null;
	return storeId
		? partNumber
			? [
					shrink({
						storeId,
						partNumber,
						langId,
						...(physicalStoreUniqueId && {
							query: { physicalStoreId: physicalStoreUniqueId, search },
						}),
					}),
					DATA_KEY_INVENTORY,
			  ]
			: productIds
			? [
					shrink({
						storeId,
						productIds,
						langId,
						...(physicalStoreUniqueId && {
							query: { physicalStoreId: physicalStoreUniqueId, search },
						}),
					}),
					DATA_KEY_INVENTORY,
			  ]
			: null
		: null;
};

/**
 * Server side inventory fetching API,
 * For HDMX, use productIds and physicalStoreUniqueIds only.
 *
 * @param cache
 * @param partNumber
 * @param productIds
 * @param context
 * @param physicalStoreIds
 * @returns
 */
export const getInventoryV2 = async (
	cache: Cache,
	partNumber: string,
	productIds: string,
	context: GetServerSidePropsContext,
	physicalStoreIds?: string[],
	search?: number
): Promise<{ key: string; value: InventoryResponse }[]> => {
	const settings = await getSettings(cache, context);
	const { localeName: locale } = await getStoreLocale({ cache, context });
	const routes = await getLocalization(cache, locale, 'Routes');
	const { storeId, langId } = getServerSideCommon(settings, context);
	const { identifier: store } = settings;
	const usesPBC = settings.userData[INVENTORY_PBC] === STRING_TRUE;
	const fulfillmentCenter = [settings.userData[INVENTORY_PBC_EXT_FFM_ID]].filter(Boolean).join(',');
	const props = usesPBC
		? { store, partNumber, productIds, fulfillmentCenter }
		: { storeId, partNumber, productIds, langId };

	let physicalStoreUniqueId = physicalStoreIds?.join(',');
	const { uniqueId } = await getDefaultStore(cache, context);
	if (!physicalStoreUniqueId) {
		physicalStoreUniqueId = uniqueId;
	}

	const key = unstable_serialize(
		getSWRKey({ settings, physicalStoreUniqueId, productIds, partNumber, langId, search })
	);

	const params = constructRequestParamsWithPreviewToken({ context, settings, routes });
	await getLocalization(cache, locale, 'Inventory');
	const value =
		(key && cache.get(key)) ||
		(usesPBC
			? fetcherPBC(
					false,
					context
			  )({ query: props as Parameters<ReturnType<typeof fetcherPBC>>[0]['query'], params })
			: fetcher(false, context)(
					storeId,
					partNumber,
					{ physicalStoreId: physicalStoreUniqueId },
					params,
					productIds
			  ));
	key && cache.set(key, value);
	return await value;
};

type Props = {
	partNumber?: string;
	productIds?: string;
	physicalStoreIds?: string[];
	physicalStore?: StoreDetails;
	search?: number;
};
/**
 * For HDMX, fetching inventory requires productIds and physicalStoreUniqueIds.
 * partNumber and physicalStore should not be used.
 *
 */
export const useInventoryV2 = ({
	partNumber,
	productIds,
	physicalStoreIds,
	physicalStore,
	search,
}: Props) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { langId } = getClientSideCommon(settings, router);
	const usesPBC = settings.userData[INVENTORY_PBC] === STRING_TRUE;
	const params = useExtraRequestParameters();
	let physicalStoreUniqueId = physicalStoreIds?.join(',');
	const { uniqueId } = useDefaultStore();
	if (!physicalStoreUniqueId) {
		physicalStoreUniqueId = uniqueId;
	}

	const limit = useMemo(
		() =>
			partNumber
				?.split(',')
				.map((_partNumber) => _partNumber.trim())
				.filter(Boolean).length,
		[partNumber]
	);
	const {
		data,
		error,
		isLoading: isLoadingTS,
		isValidating: isValidatingTS,
		mutate: mutateInventory,
	} = useSWR(
		!usesPBC
			? getSWRKey({ settings, physicalStoreUniqueId, productIds, partNumber, langId, search })
			: null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(
				props as unknown as Parameters<typeof expand>[0]
			);
			const { storeId, partNumber, productIds, query } = expanded;
			// If the SWR key was built with productIds, partNumber will be undefined.
			// We must pass the correct identifier to the fetcher.
			// credentials 'omit': inventory is public, so the browser neither sends the cookies nor stores
			// the WC_* generic-user cookies HCL answers with when there is no session.
			return fetcher(true)(
				storeId,
				partNumber ?? productIds,
				query,
				{ ...params, credentials: 'omit' },
				productIds
			);
		},
		// defect HC-34836
		{
			revalidateIfStale: true,
			dedupingInterval: INVENTORY_DEDUPING_INTERVAL,
			// Keep prior availability while the SWR key changes (e.g. cart item removed),
			// so remaining cart rows don't flash skeletons during the refetch.
			keepPreviousData: true,
		}
	);

	const {
		data: dataPBC,
		error: errorPBC,
		isLoading: isLoadingPBC,
		isValidating: isValidatingPBC,
		mutate: mutateInventoryPBC,
	} = useSWR(
		usesPBC ? getSWRKey_PBC({ settings, physicalStore, partNumber }) : null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props as Parameters<typeof expand>[0]);
			const { store, partNumber, fulfillmentCenter } = expanded;
			return fetcherPBC(true)({ query: { store, partNumber, fulfillmentCenter, limit }, params });
		},
		{
			revalidateIfStale: true,
			dedupingInterval: INVENTORY_DEDUPING_INTERVAL,
			keepPreviousData: true,
		}
	);

	const ts = useMemo(() => (data ? dataMap(data) : data), [data]);
	const pbc = useMemo(
		() => (dataPBC ? dataMapPBC(dataPBC, settings, physicalStore) : dataPBC),
		[dataPBC, physicalStore, settings]
	);
	const availability = usesPBC ? pbc : ts;
	const hasInventory = useMemo(
		() => !!availability?.find((iv: any) => iv.status || iv.physicalStoreStatus),
		[availability]
	);

	return {
		availability,
		hasInventory,
		isLoading: usesPBC ? isLoadingPBC : isLoadingTS,
		// True while a (re)fetch is in flight; until it settles `availability` may still be
		// the cached value, or (via `keepPreviousData`) the one from a previous key.
		isValidating: usesPBC ? isValidatingPBC : isValidatingTS,
		loading: usesPBC ? !errorPBC && !dataPBC : !error && !data,
		mutateInventory: usesPBC ? mutateInventoryPBC : mutateInventory,
		error,
	};
};
